import React from 'react';
import { getAdminApiError } from '../lib/adminApiErrors';
import { hasDeliverableUserEmail } from '../lib/adminUsers';
import type { AdminUserDto, AdminUserStatus } from '../types';
import { AdminIconButton, useAdminToast } from './components';

export interface UserActionsProps {
  user: AdminUserDto;
  protectedSuper: boolean;
  open: boolean;
  toggle: () => void;
  close: () => void;
  reset?: (userId: number) => Promise<unknown>;
  onChangeStatus?: (userId: number, status: AdminUserStatus) => Promise<unknown>;
}

const useUserActionMenu = (open: boolean, close: () => void) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (!open) {
      return undefined;
    }
    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      close();
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [close, open]);
  return { rootRef, triggerRef };
};

const UserActionMenuItems: React.FC<{
  busy: boolean;
  canCopy: boolean;
  canDeliver: boolean;
  canReset: boolean;
  protectedSuper: boolean;
  canChangeStatus: boolean;
  statusLabel: string;
  emailExplanationId: string;
  statusExplanationId: string;
  copyEmail: () => Promise<void>;
  resetPassword: () => Promise<void>;
  changeStatus: () => Promise<void>;
}> = (props) => (
  <>
    <button
      type="button"
      role="menuitem"
      disabled={props.busy}
      aria-disabled={props.busy || !props.canCopy}
      onClick={() => void props.copyEmail()}
    >
      Copy email
    </button>
    <button
      type="button"
      role="menuitem"
      disabled={props.busy}
      aria-disabled={props.busy || !props.canReset || !props.canDeliver}
      aria-describedby={!props.canDeliver ? props.emailExplanationId : undefined}
      onClick={() => void props.resetPassword()}
    >
      Reset password
    </button>
    {!props.canDeliver && (
      <div role="none">
        <span id={props.emailExplanationId} className="admin-user-actions__explanation">
          Password reset needs a valid email address.
        </span>
      </div>
    )}
    <button
      type="button"
      role="menuitem"
      disabled={props.busy}
      aria-disabled={props.busy || !props.canChangeStatus || props.protectedSuper}
      aria-describedby={props.protectedSuper ? props.statusExplanationId : undefined}
      onClick={() => void props.changeStatus()}
    >
      {props.statusLabel}
    </button>
    {props.protectedSuper && (
      <div role="none">
        <span id={props.statusExplanationId} className="admin-user-actions__explanation">
          The last active super administrator account cannot be blocked or deleted.
        </span>
      </div>
    )}
  </>
);

const useUserQuickActions = (
  props: UserActionsProps,
  canCopy: boolean,
  canDeliver: boolean
) => {
  const { showToast } = useAdminToast();
  const [busy, setBusy] = React.useState(false);
  const actionLock = React.useRef(false);
  const runExclusive = async (action: () => Promise<void>) => {
    if (actionLock.current) {
      return;
    }
    actionLock.current = true;
    setBusy(true);
    try {
      await action();
    } finally {
      actionLock.current = false;
      setBusy(false);
    }
  };
  const changeStatus = () => runExclusive(async () => {
    if (!props.onChangeStatus || props.protectedSuper) {
      return;
    }
    const status = props.user.status === 'blocked' ? 'active' : 'blocked';
    try {
      await props.onChangeStatus(props.user.id, status);
      showToast(status === 'blocked' ? 'User blocked.' : 'User unblocked.');
      props.close();
    } catch (error) {
      const failure = getAdminApiError(error, 'user_status_change_failed');
      showToast(`User status change failed (${failure.code}).`, { tone: 'critical' });
    }
  });
  const resetPassword = () => runExclusive(async () => {
    if (!props.reset || !canDeliver) {
      return;
    }
    try {
      await props.reset(props.user.id);
      showToast('Password reset request accepted.', { tone: 'info' });
      props.close();
    } catch (error) {
      const failure = getAdminApiError(error, 'user_reset_failed');
      showToast(`Password reset failed (${failure.code}).`, { tone: 'critical' });
    }
  });
  const copyEmail = async () => {
    if (!canCopy || !props.user.email || !navigator.clipboard) {
      showToast('The email address could not be copied.', { tone: 'critical' });
      return;
    }
    try {
      await navigator.clipboard.writeText(props.user.email);
      showToast('Email copied.');
      props.close();
    } catch {
      showToast('The email address could not be copied.', { tone: 'critical' });
    }
  };
  return { busy, changeStatus, copyEmail, resetPassword };
};

const UserActions: React.FC<UserActionsProps> = (props) => {
  const menuId = React.useId();
  const menu = useUserActionMenu(props.open, props.close);
  const canCopy = Boolean(props.user.email?.trim());
  const canDeliver = hasDeliverableUserEmail(props.user.email);
  const actions = useUserQuickActions(props, canCopy, canDeliver);
  const statusLabel = props.user.status === 'blocked' ? 'Unblock user' : 'Block user';
  const emailExplanationId = `${menuId}-email-explanation`;
  const statusExplanationId = `${menuId}-status-explanation`;

  return (
    <div
      ref={menu.rootRef}
      className="admin-user-actions"
      onClick={(event) => event.stopPropagation()}
    >
      <AdminIconButton
        ref={menu.triggerRef}
        aria-label={`User actions for ${props.user.username}`}
        aria-haspopup="menu"
        aria-expanded={props.open}
        aria-controls={props.open ? menuId : undefined}
        active={props.open}
        onClick={props.toggle}
      >
        <span aria-hidden="true">&#8942;</span>
      </AdminIconButton>
      {props.open && (
        <div id={menuId} className="admin-user-actions__menu" role="menu">
          <UserActionMenuItems
            busy={actions.busy}
            canCopy={canCopy}
            canDeliver={canDeliver}
            canReset={Boolean(props.reset)}
            protectedSuper={props.protectedSuper}
            canChangeStatus={Boolean(props.onChangeStatus)}
            statusLabel={statusLabel}
            emailExplanationId={emailExplanationId}
            statusExplanationId={statusExplanationId}
            copyEmail={actions.copyEmail}
            resetPassword={actions.resetPassword}
            changeStatus={actions.changeStatus}
          />
        </div>
      )}
    </div>
  );
};

export default UserActions;
